import { AtlasButtonProps } from "@resource/atlas";
import { DefaultTheme } from "@resource/common";
import clsx from "clsx";
import CompanyLogo from "components/CompanyLogo";
import MadeWithLove from "components/legacy/MadeWithLove/MadeWithLove";
import OrganizationThemeWrapper from "components/OrganizationThemeWrapper";
import OrganizationThemedPreviewContainer from "components/Preview/OrganizationThemedPreviewContainer";
import StageStepper, { StageStepStage } from "components/Preview/StageStepper";
import contrast from "contrast";
import { PreviewGuideOrganizationFragment } from "generated/schemaTypes";
import { find, first } from "lodash";
import { useEffect, useMemo, useState } from "react";

import ContentPreview from "./ContentPreview";
import {
  GUIDE_TEMPLATE_FOR_PREVIEW_FRAGMENT,
  JOB_ROLE_GUIDE_TEMPLATE_FOR_PREVIEW_FRAGMENT,
  PREVIEW_GUIDE_ORGANIZATION_FRAGMENT,
} from "./fragments";
import { ContentModuleForDisplay } from "./types";

export type PreviewGuideOrganization = PreviewGuideOrganizationFragment;

export type PreviewGuideStage = StageStepStage & {
  resolvedModules: ContentModuleForDisplay[];
};

export type GuidePreviewData = {
  stages: PreviewGuideStage[];
  organization: PreviewGuideOrganization;
  currentStage?: {
    id: string;
  };
  stageStepperEnabled: boolean;
};

type GuidePreviewProps = GuidePreviewData & {
  header: (props: { variant: AtlasButtonProps["variant"] }) => JSX.Element;
  showFooter?: boolean;
  className?: string;
};

function GuidePreview({
  header,
  showFooter = true,
  organization,
  currentStage,
  stages: initialStages,
  className,
  stageStepperEnabled,
}: GuidePreviewProps) {
  const [stages, setStages] = useState<PreviewGuideStage[]>(initialStages);
  const [selectedStageId, setSelectedStageId] = useState(
    currentStage?.id ?? stages[0]?.id
  );
  const theme = organization?.theme ?? DefaultTheme;

  const orgThemeBackgroundButtonVariant = useMemo(
    (): AtlasButtonProps["variant"] =>
      contrast(theme.primaryColor) === "light" ? "dark" : "light",
    [theme.primaryColor]
  );

  const selectedStage = find(stages, { id: selectedStageId });
  const selectedStageModules = selectedStage?.resolvedModules ?? null;

  useEffect(() => {
    setStages(initialStages);
    setSelectedStageId(currentStage?.id ?? initialStages[0]?.id);
  }, [initialStages, currentStage]);

  return (
    <OrganizationThemedPreviewContainer
      organization={organization}
      className={clsx(className, "pt-24 pb-8 px-12 space-y-8")}
    >
      <div className="z-50 absolute top-0 left-0 pt-4 px-6 flex justify-between items-center w-full">
        {header({ variant: orgThemeBackgroundButtonVariant })}
      </div>
      <OrganizationThemeWrapper
        theme={theme}
        className={clsx("bg-white rounded px-6 py-8 shadow-sm max-w-xl m-auto")}
      >
        {stages.length === 0 ? (
          <div className="h-48 rounded-xl flex px-6 py-28 justify-center items-center">
            <div className="text-center">
              <p className="text-body-md text-subtle mt-2">No stages</p>
            </div>
          </div>
        ) : (
          <CompanyLogo organization={organization} />
        )}
        {selectedStageModules && (
          <>
            {stageStepperEnabled && (
              <StageStepper
                currentStageId={currentStage?.id ?? first(stages)?.id}
                stages={stages}
                selectedStageId={selectedStageId}
                allowNavigationToLockedStages
                onSelectStageId={(stageId) => {
                  setSelectedStageId(stageId);
                }}
              />
            )}
            <ContentPreview modules={selectedStageModules} />
          </>
        )}
      </OrganizationThemeWrapper>
      {showFooter && <MadeWithLove />}
    </OrganizationThemedPreviewContainer>
  );
}
GuidePreview.fragments = {
  organization: PREVIEW_GUIDE_ORGANIZATION_FRAGMENT,
  guideTemplate: GUIDE_TEMPLATE_FOR_PREVIEW_FRAGMENT,
  jobRoleGuideTemplate: JOB_ROLE_GUIDE_TEMPLATE_FOR_PREVIEW_FRAGMENT,
};

export default GuidePreview;
